//
// ███ shared/comms-conversations — who a conversation admits ███
//
// T1 (brief-comms-chat, 2026-10-27). ONE domain rule with TWO enforcement
// sites, which is the shape this project has a testing convention for: when two
// predicates partition one domain concept, the invariant is asserted, not one
// example per predicate.
//
//   * `firestore.rules`' `conversationAdmits` gates every client read and write.
//   * This function gates every CLOUD FUNCTION write — and that is not
//     belt-and-braces. The Admin SDK bypasses rules entirely, so a handler that
//     does not ask this question has no gate at all. `addPost` shipped with
//     exactly that hole (membership checked, role never), and the client-side
//     `v-if` was the only thing standing in front of it.
//
// ⚠ THE RULES COPY CANNOT IMPORT THIS FILE, so the duplication is unavoidable
// and the drift is what has to be caught. `src/tests/commsConversationAdmitsAgreement.test.js`
// derives the rules clause from `firestore.rules` and compares it against this
// implementation's behaviour over a fixture matrix, the way
// `commsReactionPaletteAgreement.test.js` does for the reaction palette.
//
// ⚠ A MISMATCH IS SILENT IN THE DIRECTION THAT MATTERS. If the rules are
// stricter, a member sees a function succeed and then cannot read what it
// wrote. If THIS is stricter, nothing breaks and nobody notices — until the
// rules are relaxed and the function becomes the hidden gate.
//

// The two conversations every org has. `firestore.rules` refuses a client
// create of either id, so the only thing that can bring them into existence is
// the function that seeds them on first read.
const RESERVED_CONVERSATION_IDS = Object.freeze(['everyone', 'announcements']);

const isReservedConversation = (convId) => RESERVED_CONVERSATION_IDS.includes(convId);

/**
 * True when this conversation admits this uid.
 *
 * ⚠ THE DEFAULT IS THE RESTRICTIVE ONE, matching the rules clause exactly: a
 * conversation document with no `audience` admits only the uids it lists, and
 * one carrying neither field admits nobody. The rules version reaches that with
 * `get('audience', 'members')` because reading an absent field there is an
 * evaluation error; this one reaches it because an absent field in JavaScript is
 * `undefined`, which is not `'everyone'`. Same answer, different mechanism, and
 * the mechanisms are written down so a reader does not "simplify" one of them.
 *
 * ⚠ IT DOES NOT ASK ABOUT ORG MEMBERSHIP, and must not be mistaken for the
 * whole gate. The rules clause is `isOrgMember(orgId) && conversationAdmits(…)`;
 * a handler's equivalent is `requireOrgMember(…)` and then this. Retirement is
 * the org-membership half, not this half.
 *
 * ⚠ AND IT SAYS NOTHING ABOUT `archived`. Archiving stops writes and leaves
 * reads alone, so the two questions are separate in both enforcement sites.
 *
 * ⚠ THE UID IS TYPE-CHECKED BEFORE IT IS COMPARED, which makes this copy
 * STRICTER THAN THE RULES IN ONE CELL THE RULES CANNOT REACH — written down
 * rather than levelled, because an undocumented asymmetry is the thing the
 * agreement test exists to prevent. Measured while writing that test:
 * `[undefined].includes(undefined)` is `true`, so without this guard a caller
 * passing no uid at all is ADMITTED by any member list carrying a hole.
 * `request.auth.uid` in rules is always a non-empty string, and Firestore
 * cannot store `undefined` in an array, so neither half of that cell exists on
 * the client path; the function path builds its own objects, and `members` here
 * can be an array literal a handler assembled. Naming the blanks would be a
 * deny-list, so the TYPE is allow-listed instead.
 *
 * ⚠ AND THE GUARD SITS ABOVE THE AUDIENCE TEST, not below it, so a blank uid is
 * refused even by an `everyone` conversation. That is the ordering on purpose:
 * `audience == 'everyone'` means "every MEMBER of this org", and a caller with
 * no identity is not one of them. Exercise the ordering, not just the branch —
 * the agreement test drives a blank uid against an `everyone` document, which
 * is the cell that would pass if these two lines were swapped.
 */
function conversationAdmits(conversation, uid) {
  if (!conversation || typeof conversation !== 'object') return false;
  if (typeof uid !== 'string' || uid === '') return false;
  if (conversation.audience === 'everyone') return true;
  const members = conversation.memberIds;
  if (!Array.isArray(members)) return false;
  return members.includes(uid);
}

//
// ███ T4: MAKING ONE ███
//
// Everything below is about CREATING a conversation and curating it afterwards.
// None of it is a gate — `conversationAdmits` above is the gate — and none of it
// is reachable from the rules, which cannot iterate a list, sort one, or hash
// anything. It is shared so that the client, any future handler and the T6
// migration agree on one answer to "which DM is this".
//

// ⚠ TWO VALUES, AND `role` IS REFUSED RATHER THAN ABSENT. The ruling on T4 is
// that `audience: 'role'` is denied by `firestore.rules` and missing from the
// create form, not merely unimplemented — so there is a list of what is allowed
// AND a list of what is deliberately refused, and a test asserts the second is
// not silently empty. The reason a role audience needs a decision first is in
// `tickets/ticket-role-audience-needs-a-vocabulary.md`: this codebase has three role
// vocabularies and they need not intersect.
const CHANNEL_AUDIENCES = Object.freeze(['everyone', 'members']);
const REFUSED_AUDIENCES = Object.freeze(['role']);

// A DM holds the viewer plus 1–7 others, so 2–8 members. The cap is the
// brief's; `firestore.rules` states the same 8 independently, because the rules
// cannot import this file, and `src/tests/commsNewConversation.test.js` asserts
// the two numbers agree rather than trusting that somebody kept them in step.
const DM_MIN_MEMBERS = 2;
const DM_MAX_MEMBERS = 8;
const DM_MAX_OTHERS = DM_MAX_MEMBERS - 1;

const CHANNEL_NAME_MAX = 80;

/**
 * Sort, de-duplicate and type-check a member list.
 *
 * ⚠ IT RETURNS null RATHER THAN A CLEANED-UP LIST when anything is wrong, and
 * the caller must branch on that. A function that silently drops a bad entry
 * would turn "you picked somebody who no longer exists" into a DM with fewer
 * people than the manager chose, and the difference would surface as a missing
 * colleague rather than as an error.
 *
 * ⚠ `/` IS REFUSED, not escaped. These uids become part of a DOCUMENT ID below,
 * and a `/` in a document id is a path separator — it would silently write to a
 * different collection. Auth0 subs do not contain one; a hand-built fixture can.
 */
function normaliseMemberIds(uids) {
  if (!Array.isArray(uids)) return null;
  const seen = new Set();
  for (const uid of uids) {
    if (typeof uid !== 'string' || uid === '') return null;
    if (uid.includes('/')) return null;
    seen.add(uid);
  }
  if (seen.size === 0) return null;
  return [...seen].sort();
}

/**
 * The id a DM between exactly these people has, whoever creates it.
 *
 * ⚠ DETERMINISTIC ON PURPOSE, AND IT IS THE RACE GUARD RATHER THAN THE DEDUPE.
 * The dedupe is `findExistingDm` below, which compares member SETS and so also
 * finds a DM created before this scheme or by the migration. What a derived id
 * buys is the case no set comparison can cover: two people opening a DM with
 * each other at the same moment. Both compute the same id, so the second write
 * lands on an existing document — where it is an UPDATE, and the update rule's
 * allowlist refuses a write carrying `type` and `createdBy` — and the client
 * then resolves to the conversation that is already there.
 *
 * ⚠ IT IS NOT HASHED, and that is a decision. A hash would be shorter and would
 * hide the member ids from the document path; `firestore.rules` has
 * `hashing.sha256`, but nothing in this repository uses it and an unproven rules
 * primitive fails CLOSED and invisibly, which is this file's most expensive
 * recurring defect. A joined id is also checkable by eye in a console. The ids
 * are already inside the document for anyone permitted to read it, so the path
 * discloses nothing the document does not.
 *
 * ⚠ AND THE RULES DO NOT VERIFY IT. Rules cannot sort a list, so they cannot
 * confirm an id was derived from its own member list. A client that mis-sorts
 * deliberately can therefore still create a second DM with the same members
 * under a different id; the consequence is a duplicate row for the people in it,
 * not a disclosure, because the read rule still only admits members. A server
 * chokepoint is the fix if that ever matters, and it is named here so the gap is
 * not mistaken for one nobody noticed.
 */
function dmConversationId(uids) {
  const members = normaliseMemberIds(uids);
  if (!members) return null;
  if (members.length < DM_MIN_MEMBERS || members.length > DM_MAX_MEMBERS) return null;
  return `dm_${members.join('_')}`;
}

/**
 * The member list a channel must end up with when THIS person limits it.
 *
 * ⚠ A CHANNEL LIMITED TO CHOSEN PEOPLE KEEPS THE PERSON WHO LIMITED IT, and the
 * reason is a measured refusal rather than a courtesy. The conversation read
 * rule in `firestore.rules` admits `audience == 'everyone'` or a uid in
 * `memberIds` and nothing else, so a manager who narrows a channel to a list
 * without themselves is denied the very next read of it. The update rule never
 * reads `memberIds` for a channel, so they could still widen it back blind — but
 * the list every screen is built from is `audience == 'everyone'` unioned with
 * `memberIds array-contains me`, so the channel has left their list and there is
 * no screen to do it from. The channel is orphaned, with its history intact and
 * nobody able to reach it.
 *
 * ⚠ THE RULES NOW ENFORCE THIS TOO, AND THEY DID NOT WHEN THIS WAS WRITTEN.
 * T4 shipped this function as the ONLY guard, because the read rule admits
 * nobody a list omits and rules cannot prove "at least one member who can still
 * find this" — that needs a per-uid lookup over a list. T4b (2026-10-27) found
 * the narrower invariant that rules CAN express: `writerKeepsAccess` in
 * `firestore.rules` requires a members channel to contain the uid writing to it,
 * on create and on update. So a client bypassing both screens can no longer
 * orphan a channel.
 *
 * This function stays, and is not now redundant. It is what makes the refusal
 * never happen: the rules answer a write that has already been composed, and
 * this composes a write that will be accepted. The pinned row and its sentence
 * are the reader's half of the same thing.
 *
 * ⚠ WHAT IS STILL NOT ENFORCED ANYWHERE: a second manager may remove the first
 * (deliberately — the writer remains, so the channel keeps a reader), and a
 * channel whose last remaining member is later retired or deleted loses that
 * reader with no write to refuse.
 *
 * Returns null on the same inputs `normaliseMemberIds` rejects, so a caller
 * cannot turn a bad list into a good one by passing it through here.
 */
function membersWithActor(memberIds, actorUid) {
  if (typeof actorUid !== 'string' || actorUid === '') return null;
  const base = Array.isArray(memberIds) ? memberIds : [];
  return normaliseMemberIds([...base, actorUid]);
}

/**
 * True when these two lists name the same people, order and duplicates aside.
 */
function sameMemberSet(a, b) {
  const left = normaliseMemberIds(a);
  const right = normaliseMemberIds(b);
  if (!left || !right) return false;
  if (left.length !== right.length) return false;
  return left.every((uid, i) => uid === right[i]);
}

/**
 * The existing DM with exactly this member set, or null.
 *
 * ⚠ BY MEMBER SET, NOT BY ID, and the viewer's own listener is what makes that
 * complete: the conversation list is the union of an `everyone` query and a
 * `memberIds array-contains <me>` query, and a DM this viewer belongs to must
 * satisfy the second. So if a DM with these people exists at all, it is in the
 * list this searches — and a set comparison finds it whatever its id, including
 * the ids T6 will write.
 *
 * ⚠ IT IGNORES `archived`. An archived DM with the same people is still that
 * DM; resolving to it is right, and the settings sheet is where it gets
 * unarchived. Creating a second one would leave two threads with one history
 * split between them.
 */
function findExistingDm(conversations, uids) {
  const wanted = normaliseMemberIds(uids);
  if (!wanted || !Array.isArray(conversations)) return null;
  return conversations.find((c) => c
    && c.type === 'dm'
    && sameMemberSet(c.memberIds, wanted)) || null;
}

// ███ THE REFUSALS, AS SENTENCES ███
//
// Each one states what is wrong and what the reader can do, and names no
// position: no "below", "above", "here" or "this field". They live here rather
// than in the component so the component cannot invent a tenth wording, and so
// the copy scanner reads them from one file.
const NAME_REQUIRED = 'A channel needs a name before it can be created.';
const NAME_TOO_LONG = `A channel name can be up to ${CHANNEL_NAME_MAX} characters.`;
const MEMBERS_REQUIRED = 'A members-only channel needs at least one person chosen.';
const DM_NEEDS_SOMEBODY = 'Choose at least one person to start a direct message.';
// Shown wherever `membersWithActor` is about to act, so the fixed row in the
// picker is explained rather than merely unremovable.
const ACTOR_KEEPS_ACCESS = 'You are kept in a channel you limit to chosen people, because a channel you are not in does not appear in your own list.';
const DM_TOO_MANY = `A direct message holds up to ${DM_MAX_OTHERS} other people. Start a channel for a larger group.`;
const ARCHIVED_NO_POSTING = 'This conversation is archived, so it cannot take new messages. A manager can reopen it.';

// ███ THE PEOPLE PICKER'S OWN COPY AND PREDICATE ███ (T6, 2026-10-28)
//
// The picker is one component used by both modes of `NewConversationDialog.vue`.
// Its search predicate and its count sentence live here with the refusals, for
// the same two reasons: the component cannot invent a second wording, and the
// copy scanner reads every sentence from one file.

/** The search field's label. A name, not a position — it labels its own input. */
const PICKER_SEARCH_LABEL = 'Search people';

/**
 * Shown when the search excludes everybody, which is NOT the same as having
 * nobody to show. The two states have separate sentences because a reader who
 * typed a search needs to know their typing caused it; `PICKER_NOBODY` is the
 * organisation having no other active person at all.
 */
const PICKER_NO_MATCH = 'No active person matches that search.';
const PICKER_NOBODY = 'Nobody else is active in this organisation yet.';

/**
 * Does this person's name match the search?
 *
 * ⚠ BY NAME ONLY, AND SAID SO. It does not search uids: a uid is an Auth0 `sub`
 * and typing one is not something a venue manager does, while matching against
 * it would make a search for "sam" also return somebody whose uid happened to
 * contain those letters, with no visible reason on the row.
 *
 * ⚠ AND AN EMPTY OR BLANK QUERY MATCHES EVERYBODY rather than nobody. The
 * no-filter state is the common one — the field exists for a long list — and a
 * predicate that returned `false` for `''` would show an empty picker until
 * something was typed.
 */
function peopleSearchMatches(label, query) {
  const needle = typeof query === 'string' ? query.trim().toLowerCase() : '';
  if (needle === '') return true;
  const hay = typeof label === 'string' ? label.toLowerCase() : '';
  return hay.includes(needle);
}

/**
 * The sentence above the chips, carrying the count.
 *
 * ⚠ IT NAMES ITS POPULATION, because the number and the membership differ. In a
 * members-only channel the creator is a member too — `membersWithActor` adds
 * them and `writerKeepsAccess` in `firestore.rules` refuses the write without
 * them — so "3 people chosen" beside three chips would be a number describing a
 * set of four. `pinned` is exactly the state in which that is true, and the
 * sentence changes rather than the number.
 *
 * ⚠ THE COUNT AND THE SENTENCE COME FROM THIS ONE PLACE. The component renders
 * one chip per entry of the same array it passes the length of, so there is no
 * second count to disagree with this one.
 */
function chosenCountSentence(count, { pinned = false } = {}) {
  const n = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  if (n === 0) return pinned ? 'Nobody chosen yet, so the channel would be you alone.' : '';
  const people = n === 1 ? '1 person' : `${n} people`;
  return pinned
    ? `${people} chosen, and you — ${n + 1} members in all.`
    : `${people} chosen.`;
}

/**
 * Validate a proposed channel. Returns a refusal sentence, or '' when it is fine.
 *
 * ⚠ IT CHECKS THE AUDIENCE AGAINST THE ALLOWED LIST, NOT AGAINST THE REFUSED
 * ONE. An allow-list of what is permitted fails closed when a fourth audience
 * is invented; a deny-list naming only `role` would let `location` through.
 */
function channelRefusal({ name, audience, memberIds }) {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  if (trimmed === '') return NAME_REQUIRED;
  if (trimmed.length > CHANNEL_NAME_MAX) return NAME_TOO_LONG;
  if (!CHANNEL_AUDIENCES.includes(audience)) return NAME_REQUIRED;
  if (audience === 'members' && !normaliseMemberIds(memberIds)) return MEMBERS_REQUIRED;
  return '';
}

/**
 * Validate a proposed DM, given the people chosen BESIDES the viewer.
 */
function dmRefusal(otherUids) {
  const others = normaliseMemberIds(otherUids);
  if (!others) return DM_NEEDS_SOMEBODY;
  if (others.length > DM_MAX_OTHERS) return DM_TOO_MANY;
  return '';
}

/**
 * True when this viewer may leave this conversation.
 *
 * ⚠ IT IS HERE RATHER THAN IN A COMPONENT BECAUSE TWO SURFACES ASK IT. The
 * settings sheet has asked since T4 and the thread header asks from S3; a
 * second copy of 'isDm && memberIds.includes(uid)' would be the fifth member of a
 * family this repository has already filed three tickets about, and the two
 * would disagree the day one of them learned that a blank uid is not a member.
 *
 * ⚠ ONLY A DM. Leaving a channel is not an action this product offers: removing
 * yourself from a members channel writes a list you are not in, the read rule
 * then refuses you the channel, and no list query returns it — the orphaning
 * argued at 'membersWithActor'. An 'everyone' channel has no list to leave.
 *
 * ⚠ AND THE UID IS TYPE-CHECKED BEFORE IT IS COMPARED, for the reason
 * 'conversationAdmits' measures above: '[undefined].includes(undefined)' is 'true',
 * so a caller passing no uid at all would be offered a Leave control on any DM
 * whose member list carries a hole. Allow-list the TYPE; naming the blanks
 * would be a deny-list.
 */
function canLeaveConversation(conversation, uid) {
  if (typeof uid !== 'string' || uid === '') return false;
  if (!conversation || typeof conversation !== 'object') return false;
  if (conversation.type !== 'dm') return false;
  const members = conversation.memberIds;
  return Array.isArray(members) && members.includes(uid);
}

/**
 * True when this viewer may pin and unpin messages in this conversation.
 *
 * ⚠ THIS IS A COPY OF A RULE, AND THE RULE IS THE ONE THAT DECIDES. The
 * `allow update` branch on a conversation lets `pinnedMessageIds` be written
 * only when `resource.data.type == 'channel'` AND `callerIsManager(orgId)`; the
 * other branch — every non-channel, which is every DM — allows a self-only
 * `memberIds` delta and NOTHING else. So a Pin control shown on a DM is a
 * control whose write the database refuses, whoever clicks it.
 *
 * ⚠ WHICH IS WHY IT IS `type === 'channel'` AND NOT `type !== 'dm'`. The two
 * read the same today, because those are the only two types a conversation is
 * born with — and they come apart the moment a third is added, in the direction
 * that grants rather than the direction that refuses. The rules say 'channel',
 * so this says 'channel'.
 *
 * ⚠ THE MANAGER FLAG IS THE CALLER'S, NOT READ OFF THE CONVERSATION. The client
 * cannot evaluate `callerIsManager`; it knows only what the permission store
 * says. This predicate therefore decides what to SHOW, and the rules decide what
 * lands. A viewer who is wrong about being a manager sees a control that fails,
 * which is a visible refusal rather than a silent write.
 */
function canPinMessages(conversation, isManager) {
  if (isManager !== true) return false;
  if (!conversation || typeof conversation !== 'object') return false;
  return conversation.type === 'channel';
}

/**
 * True when this viewer may delete this message or reply: its AUTHOR, or a
 * manager.
 *
 * ⚠ ONE PREDICATE, CALLED FROM BOTH SIDES, AND THAT IS THE POINT. The server's
 * `deleteCommsMessage` asks it to decide whether to answer 403; the thread asks
 * it to decide whether to draw the control. Written twice, the two would come
 * apart in the direction that offers a control the server refuses — the shape
 * `CLAUDE.md` names for two predicates partitioning one domain concept across
 * the `src/` / `functions/` boundary, where no single file shows both.
 *
 * ⚠ IT IS NOT A COPY OF A DATABASE RULE, unlike `canPinMessages` above.
 * `firestore.rules` is `allow delete: if false` on messages and on replies, so
 * NO client delete is permitted at all and there is no rule to mirror: the
 * function is the only path, and this predicate is the whole of what it allows.
 *
 * ⚠ THE AUTHOR IS COMPARED BY TYPE FIRST. A legacy message with no `authorId`
 * and a caller with no uid would otherwise match as `undefined === undefined`
 * and hand a stranger the control — the same hole `conversationAdmits` measures.
 * Blank is not a match; allow-list the TYPE before comparing.
 *
 * ⚠ ARCHIVED IS DELIBERATELY NOT A CONDITION. Archiving retires a channel, and
 * a venue that archived a channel holding something that should not be there
 * would otherwise have no way to remove it — the reason `deleteCommsMessage`
 * states for not calling `requireWritable`.
 */
function canDeleteMessage({ authorId, uid, isManager } = {}) {
  if (isManager === true) return true;
  if (typeof uid !== 'string' || uid === '') return false;
  if (typeof authorId !== 'string' || authorId === '') return false;
  return authorId === uid;
}

module.exports = {
  RESERVED_CONVERSATION_IDS,
  isReservedConversation,
  conversationAdmits,
  canLeaveConversation,
  canPinMessages,
  canDeleteMessage,
  CHANNEL_AUDIENCES,
  REFUSED_AUDIENCES,
  DM_MIN_MEMBERS,
  DM_MAX_MEMBERS,
  DM_MAX_OTHERS,
  CHANNEL_NAME_MAX,
  normaliseMemberIds,
  membersWithActor,
  dmConversationId,
  sameMemberSet,
  findExistingDm,
  channelRefusal,
  dmRefusal,
  NAME_REQUIRED,
  NAME_TOO_LONG,
  MEMBERS_REQUIRED,
  DM_NEEDS_SOMEBODY,
  ACTOR_KEEPS_ACCESS,
  DM_TOO_MANY,
  ARCHIVED_NO_POSTING,
  PICKER_SEARCH_LABEL,
  PICKER_NO_MATCH,
  PICKER_NOBODY,
  peopleSearchMatches,
  chosenCountSentence,
};
